import React, { CSSProperties } from 'react';
import { Draggable, DraggableProps, DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import { BpDesignSystem } from '../../types';
import { styled } from '../../styles';
import { Box } from '../Box';
import { mergeClassNames } from '../../utils/cssClassNameHelper';
import { IconName } from '../Icon/types';
import { Icon } from '../Icon/Icon';

type DragAndDropItemProps = BpDesignSystem.Props<{
  slotProps?: {
    draggable?: Omit<DraggableProps, 'children' | 'draggableId' | 'index' | 'isDragDisabled'>;
  };
  draggableId: string;
  index: number;
  disabled?: boolean;
  selected?: boolean;
  getStyle?: (isDragging: boolean, providedStyles?: CSSProperties) => CSSProperties;

  /* If you want to display an icon  */
  icon?:
    | boolean
    | {
        name?: IconName; // by default: drag_indicator
        dragHandler?: boolean; // by default false. Set this to true if you want to be able to drag an entire item by dragging only its icon.
      };
  /* If you want a custom drag handler, such as an icon, use this property instead of "children" and pass the "dragHandleProps" to your handler. */
  getChildren?: (dragHandleProps?: DraggableProvidedDragHandleProps | undefined) => React.ReactNode;
  /* In this case, the drag handler is the entire component. */
  children?: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}>;

export const DragAndDropItem: React.FC<DragAndDropItemProps> = ({
  className,
  dataTest,
  style,
  sx,
  children,
  slotProps,
  disabled,
  index,
  getStyle,
  draggableId,
  getChildren,
  onClick,
  selected,
  icon
}) => {
  return (
    <Draggable draggableId={draggableId} index={index} isDragDisabled={disabled} {...slotProps?.draggable}>
      {(provided, snapshot) => {
        const iconHandler = typeof icon === 'object' && !!icon.dragHandler;
        return (
          <StyledBox
            $selected={!!selected}
            $isDragging={snapshot.isDragging}
            className={mergeClassNames(className, { disabled: disabled })}
            dataTest={dataTest}
            sx={sx}
            ref={provided.innerRef}
            onClick={!disabled ? onClick : undefined}
            {...provided.draggableProps}
            {...(children && !iconHandler ? provided.dragHandleProps : undefined)}
            style={{
              ...style,
              ...provided.draggableProps.style,
              ...getStyle?.(snapshot.isDragging, provided.draggableProps.style)
            }}
          >
            {icon ? (
              <Box {...(iconHandler ? provided.dragHandleProps : {})} className="icon-wrapper">
                <Icon name={typeof icon !== 'boolean' ? (icon?.name ?? 'drag_indicator') : 'drag_indicator'} />
              </Box>
            ) : (
              <span />
            )}
            {children || getChildren?.(provided.dragHandleProps)}
          </StyledBox>
        );
      }}
    </Draggable>
  );
};

const StyledBox = styled(Box)<{ $selected: boolean; $isDragging?: boolean }>`
    transition: background-color 0.2s ease-in-out;
    min-height: 32px;
    background-color: ${({ theme, $selected, $isDragging }) => ($selected ? theme.tokens.colorSecondaryContainer : $isDragging ? theme.tokens.stateOnSurfaceDraggedStateLayerColor : undefined)};
    padding: ${({ theme }) => `${theme.spacingSize.xs} ${theme.spacingSize.m}`};
    display: grid;
    grid-template-columns: auto 1fr auto;
    grid-gap: ${({ theme }) => theme.spacingSize.m};
    align-items: center;
    border-radius: ${({ theme }) => theme.borderRadius.m};
    
    .icon-wrapper {
        display: inline-flex;
    }

    &.disabled {
      cursor: default;
      pointer-events: none;
      user-select: none;
        
      color: ${({ theme }) => theme.tokens.stateOnSurfaceDisabledContentStateLayerColor};
        [data-icon-name] {
            color: ${({ theme }) => theme.tokens.stateOnSurfaceDisabledContentStateLayerColor};
        }
    }

    &:hover:not(.disabled) {
        background-color: ${({ theme, $selected }) => ($selected ? theme.tokens.colorInversePrimary : '#efefef')};
    }
`;
